<script setup>
  import { computed } from 'vue';
  import { formatLocalized } from '@/composables/useDateHelper';
  import DatePicker from '@/components/DatePicker.vue';
  import Icon from "@/components/icons/Icon.vue";
  import { isToday, isTomorrow, isYesterday } from 'date-fns'
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  const selectedDate = defineModel('selectedDate', { default: new Date() });
  const datePickerValue = defineModel('datePickerValue');

  const emits = defineEmits(['dateSelected', 'previousMonth', 'previousWeek', 'previousDay', 'today', 'nextDay', 'nextWeek', 'nextMonth']);

  const props = defineProps({
    teleportJumpControls: {
      type: Boolean,
      default: false
    },
    teleportTarget: {
      type: [ String, Object],
      required: false,
      default: null
    }
  });

  const dateDay = computed(() => {
    const date = new Date(selectedDate.value);
    if (isToday(date)) return `${t('containers.tides.today')} `;
    if (isTomorrow(date)) return `${t('containers.tides.tomorrow')} `;
    if (isYesterday(date)) return `${t('containers.tides.yesterday')} `;
    return formatLocalized(date, 'cccc');
  });

</script>

<template>
  <div class="jwx-date-toolbar">  
    <DatePicker 
      @dateSelected="emits('dateSelected', $event)"
      v-model:selectedDate="datePickerValue"
    />
    <p class="jwx-date-toolbar__selected-date">
      <span class="jwx-date-toolbar__selected-date-day">{{ dateDay }}</span>
      {{ selectedDate }}
    </p>
    <Teleport defer :disabled="!props.teleportJumpControls" :to="props.teleportTarget">
      <div class="jwx-date-toolbar__jump-controls">
        <button
          @click.prevent="emits('previousMonth')"
          class="jwx-date-toolbar__jump-control"
          data-jump-period="month"
        >
          <Icon name="chevron-triple-left" class="jwx-date-toolbar__jump-control-icon" />
        </button>
        <button
          @click.prevent="emits('previousWeek')"
          class="jwx-date-toolbar__jump-control"
          data-jump-period="week"
        >
          <Icon name="chevron-double-left" class="jwx-date-toolbar__jump-control-icon" />
        </button>
        <button
          @click.prevent="emits('previousDay')"
          class="jwx-date-toolbar__jump-control"
          data-jump-period="day"
        >
          <Icon name="chevron-left" class="jwx-date-toolbar__jump-control-icon" />
        </button>
        <button
          @click.prevent="emits('today')"
          class="jwx-date-toolbar__jump-control"
          data-jump-period="today"
        >
          <Icon name="circle-filled" class="jwx-date-toolbar__jump-control-icon" />
        </button>
        <button
          @click.prevent="emits('nextDay')"
          class="jwx-date-toolbar__jump-control"
          data-jump-period="day"
        >
          <Icon name="chevron-right" class="jwx-date-toolbar__jump-control-icon" />
        </button>
        <button
          @click.prevent="emits('nextWeek')"
          class="jwx-date-toolbar__jump-control"
          data-jump-period="week"
        >
          <Icon name="chevron-double-right" class="jwx-date-toolbar__jump-control-icon" />
        </button>
        <button
          class="jwx-date-toolbar__jump-control"
          @click.prevent="emits('nextMonth')"
          data-jump-period="month"
        >
          <Icon name="chevron-triple-right" class="jwx-date-toolbar__jump-control-icon" />
        </button>
      </div>
    </Teleport>
  </div>
</template>